import { startProfileMutation } from 'dashboard/permissionSets/profileMutations';
import {
  captureProfileSession,
  completeProfileRead,
  invalidateProfileReads,
  settleProfileReads,
  startProfileRead,
  trackProfileRead,
} from 'dashboard/permissionSets/profileReads';
import types from '../mutation-types';
import authAPI from '../../api/auth';

import { setUser, clearCookiesOnLogout } from '../utils/api';
import SessionStorage from 'shared/helpers/sessionStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';

const initialState = {
  currentUser: {
    id: null,
    account_id: null,
    accounts: [],
    email: null,
    name: null,
  },
  uiFlags: {
    isFetching: true,
  },
};

// getters
export const getters = {
  isLoggedIn($state) {
    return !!$state.currentUser.id;
  },

  getCurrentUserID($state) {
    return $state.currentUser.id;
  },

  getUISettings($state) {
    return $state.currentUser.ui_settings || {};
  },

  getAuthUIFlags($state) {
    return $state.uiFlags;
  },

  getCurrentUserAvailability($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.availability;
  },

  getCurrentUserAutoOffline($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.auto_offline;
  },

  getCurrentAccountId(_, __, rootState) {
    if (rootState.route.params && rootState.route.params.accountId) {
      return Number(rootState.route.params.accountId);
    }
    return null;
  },

  getCurrentRole($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.role;
  },

  getCurrentCustomRoleId($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.custom_role_id;
  },

  getCurrentUser($state) {
    return $state.currentUser;
  },

  getMessageSignature($state) {
    const { message_signature: messageSignature } = $state.currentUser;

    return messageSignature || '';
  },

  getCurrentAccount($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount || {};
  },

  getUserAccounts($state) {
    const { accounts = [] } = $state.currentUser;
    return accounts;
  },
};

const readProfile = async (context, preserveUISettings) => {
  const isCurrent = startProfileRead(context);
  try {
    const response = await authAPI.validityCheck();
    if (!isCurrent()) return null;
    const profile = response.data.payload.data;
    // Access revalidation refreshes permissions only: UI settings such as the
    // layout are written by each device and must not leak between them.
    const currentUser = preserveUISettings
      ? { ...profile, ui_settings: context.state.currentUser.ui_settings }
      : profile;
    setUser(currentUser);
    context.commit(types.SET_CURRENT_USER, currentUser);
    completeProfileRead(context);
    return true;
  } catch (error) {
    if (!isCurrent()) return null;
    if (error?.response?.status === 401) clearCookiesOnLogout();
    return false;
  }
};

// actions
export const actions = {
  // true: applied; false: failed while current; null: superseded by a newer
  // profile read/write or by another session. With `coalesce`, a superseded
  // read resolves with the newer read that superseded it (or true when a newer
  // profile write was applied), so concurrent callers never re-read in a loop.
  async validityCheck(
    context,
    { preserveUISettings = false, coalesce = false } = {}
  ) {
    const sameSession = captureProfileSession(context);
    const read = trackProfileRead(
      context,
      readProfile(context, preserveUISettings)
    );
    const result = await read;
    if (result !== null || !coalesce || !sameSession()) return result;
    return (await settleProfileReads(context, read)) ?? true;
  },
  async setUser({ commit, dispatch }) {
    if (authAPI.hasAuthCookie()) {
      await dispatch('validityCheck');
    } else {
      commit(types.CLEAR_USER);
    }
    commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: false });
  },
  logout({ commit }) {
    invalidateProfileReads();
    commit(types.CLEAR_USER);
  },

  updateProfile: async (context, params) => {
    const request = startProfileMutation(context);
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await authAPI.profileUpdate(params);
      await request.apply(response.data);
    } catch (error) {
      throw error;
    }
  },

  updatePassword: async (context, params) => {
    const request = startProfileMutation(context);
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await authAPI.profilePasswordUpdate(params);
      await request.apply(response.data);
    } catch (error) {
      throw error;
    }
  },

  deleteAvatar: async context => {
    const request = startProfileMutation(context);
    try {
      const response = await authAPI.deleteAvatar();
      await request.apply(response.data);
    } catch (error) {
      // Ignore error
    }
  },

  updateUISettings: async (context, params) => {
    const { commit } = context;
    const request = startProfileMutation(context);
    try {
      commit(types.SET_CURRENT_USER_UI_SETTINGS, params);

      const isImpersonating = SessionStorage.get(
        SESSION_STORAGE_KEYS.IMPERSONATION_USER
      );

      if (!isImpersonating) {
        const response = await authAPI.updateUISettings(params);
        await request.apply(response.data);
      }
    } catch (error) {
      // Ignore error
    }
  },

  updateAvailability: async (context, params) => {
    const { commit, dispatch, getters: _getters } = context;
    const request = startProfileMutation(context);
    const previousStatus = _getters.getCurrentUserAvailability;

    try {
      // optimisticly update current status
      commit(types.SET_CURRENT_USER_AVAILABILITY, params.availability);
      const response = await authAPI.updateAvailability(params);
      if (!request.isCurrent()) {
        await request.apply(response.data);
        return;
      }
      const userData = response.data;
      const { id } = userData;
      await request.apply(response.data);
      if (!request.sameSession()) return;
      dispatch('agents/updateSingleAgentPresence', {
        id,
        availabilityStatus: params.availability,
      });
    } catch (error) {
      // revert back to previous status if update fails
      if (request.isCurrent())
        commit(types.SET_CURRENT_USER_AVAILABILITY, previousStatus);
    }
  },

  updateAutoOffline: async (context, { accountId, autoOffline }) => {
    const { commit, getters: _getters } = context;
    const request = startProfileMutation(context);
    const previousAutoOffline = _getters.getCurrentUserAutoOffline;

    try {
      commit(types.SET_CURRENT_USER_AUTO_OFFLINE, autoOffline);
      const response = await authAPI.updateAutoOffline(accountId, autoOffline);
      await request.apply(response.data);
    } catch (error) {
      if (request.isCurrent())
        commit(types.SET_CURRENT_USER_AUTO_OFFLINE, previousAutoOffline);
    }
  },

  setCurrentUserAvailability({ commit, state: $state }, data) {
    if (data[$state.currentUser.id]) {
      commit(types.SET_CURRENT_USER_AVAILABILITY, data[$state.currentUser.id]);
    }
  },

  setActiveAccount: async (_, { accountId }) => {
    try {
      await authAPI.setActiveAccount({ accountId });
    } catch (error) {
      // Ignore error
    }
  },

  resetAccessToken: async context => {
    const request = startProfileMutation(context);
    try {
      const response = await authAPI.resetAccessToken();
      await request.apply(response.data);
      return true;
    } catch (error) {
      return false;
    }
  },

  resendConfirmation: async () => {
    try {
      await authAPI.resendConfirmation();
    } catch (error) {
      // Ignore error
    }
  },
};

// mutations
export const mutations = {
  [types.SET_CURRENT_USER_AVAILABILITY](_state, availability) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, availability, availability_status: availability };
      }
      return account;
    });
    _state.currentUser = {
      ..._state.currentUser,
      accounts,
    };
  },
  [types.SET_CURRENT_USER_AUTO_OFFLINE](_state, autoOffline) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, autoOffline: autoOffline };
      }
      return account;
    });

    _state.currentUser = {
      ..._state.currentUser,
      accounts,
    };
  },
  [types.UPDATE_CURRENT_ACCOUNT_ATTRIBUTES](
    _state,
    { accountId, customAttributes }
  ) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === accountId) {
        return {
          ...account,
          custom_attributes: {
            ...account.custom_attributes,
            ...customAttributes,
          },
        };
      }
      return account;
    });
    _state.currentUser = {
      ..._state.currentUser,
      accounts,
    };
  },
  [types.RESET_ONBOARDING](_state, accountId) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === accountId) {
        const { onboarding_step, ...rest } = account;
        return rest;
      }
      return account;
    });

    _state.currentUser = {
      ..._state.currentUser,
      accounts,
    };
  },
  [types.CLEAR_USER](_state) {
    _state.currentUser = initialState.currentUser;
  },
  [types.SET_CURRENT_USER](_state, currentUser) {
    _state.currentUser = currentUser;
  },
  [types.SET_CURRENT_USER_UI_SETTINGS](_state, { uiSettings }) {
    _state.currentUser = {
      ..._state.currentUser,
      ui_settings: {
        ..._state.currentUser.ui_settings,
        ...uiSettings,
      },
    };
  },

  [types.SET_CURRENT_USER_UI_FLAGS](_state, { isFetching }) {
    _state.uiFlags = { isFetching };
  },
};

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};
